import {
  Accordion,
  Group,
  MultiSelect,
  Radio,
  Select,
  Stack,
  Text,
  TextInput
} from '@mantine/core';
import SegmentedInput from 'components/SegmentedInput';
import { YES_NO_OPTION } from 'constant/course';
import React, { useEffect } from 'react';
import styles from './styles.module.css';
import { Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { courseService } from 'services/lcms/course.service';
import { InputInformation } from 'modules/pages/lcms/courses/InputInformation';
import RichTextEditorInput from 'components/RichTextEditorInput';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { CourseFormData } from 'store/createCourseStore';

interface BCLDCustomFieldFormProps {
  control: any;
  errors: any;
  passingFormulaValue?: string;
  setValue: UseFormSetValue<CourseFormData>;
}

const params = {
  pageSize: 1000,
  pageNumber: 1
};

const BCLDCustomFieldForm: React.FC<BCLDCustomFieldFormProps> = ({
  control,
  errors,
  passingFormulaValue,
  setValue
}) => {
  const { data: dataBands, isPending: loadingBands } = useQuery({
    queryKey: ['getBands'],
    queryFn: () => courseService.getBands(params),
    staleTime: Infinity
  });

  const { data: dataLevelOfTraining, isPending: loadingLevelOfTraining } = useQuery({
    queryKey: ['getLevelOfTraining'],
    queryFn: () => courseService.getLevelOfTraining(params),
    staleTime: Infinity
  });

  const { data: dataTrainingType, isPending: loadingTrainingType } = useQuery({
    queryKey: ['getTrainingType'],
    queryFn: () => courseService.getTrainingType(params),
    staleTime: Infinity
  });

  const { data: dataPassingFormula } = useQuery({
    queryKey: ['getPassingFormula'],
    queryFn: () => courseService.getPassingFormula(params),
    staleTime: Infinity
  });

  const { data: dataMasterArea, isPending: loadingMasterArea } = useQuery({
    queryKey: ['masterAreaServiceGetList'],
    queryFn: () => masterAreaService.getList({ page: params.pageNumber, limit: params.pageSize }),
    staleTime: Infinity
  });

  const { data: dataCompetencyCategories, isPending: loadingCompetencyCategories } = useQuery({
    queryKey: ['getCompetencyCategories'],
    queryFn: () => courseService.getCompetencyCategories(params)
  });

  const { data: dataBusinessUnit, isPending: loadingBusinessUnit } = useQuery({
    queryKey: ['getBusinessUnitCreateLCMS'],
    queryFn: () => courseService.getBusinessUnitCreateLCMS()
  });

  // Watch form values
  const levelOfTraining = useWatch({ control, name: 'levelOfTraining' });
  const bcldLevel = useWatch({ control, name: 'bcldLevel' });

  useEffect(() => {
    if (passingFormulaValue && dataPassingFormula?.data?.length > 0) {
      setValue(
        'formulaID',
        dataPassingFormula?.data
          ?.find(
            (item: { id: number; option: string; optionDesc: string }) =>
              item.option === passingFormulaValue
          )
          ?.id?.toString() || ''
      );
    }
  }, [passingFormulaValue, dataPassingFormula, setValue]);

  useEffect(() => {
    if (dataLevelOfTraining?.data?.length > 0) {
      if (levelOfTraining) {
        const selectedItem = dataLevelOfTraining.data.find(
          (item: { id: number; option: string }) => item.option === levelOfTraining
        );

        if (selectedItem) {
          const expectedId = selectedItem.id.toString();
          if (!bcldLevel || bcldLevel === levelOfTraining || bcldLevel !== expectedId) {
            setValue('bcldLevel', expectedId, { shouldDirty: false });
          }
        }
      } else {
        if (!bcldLevel || bcldLevel === '') {
          setValue('bcldLevel', '1', { shouldDirty: false });
        }
      }
    }
  }, [levelOfTraining, bcldLevel, dataLevelOfTraining, setValue]);

  return (
    <Accordion.Item className={styles.accordionItem} value='bcldCustomField'>
      <Accordion.Control className={styles.accordionControl}>BCL&D Custom Field</Accordion.Control>
      <Accordion.Panel className={styles.accordionPanel}>
        <Stack gap='lg' py={16}>
          <Controller
            name='creatorCode'
            control={control}
            render={({ field }) => (
              <Select
                label='Category'
                placeholder='Select category'
                data={
                  dataMasterArea?.data?.map((item: any) => ({
                    value: item.areaID,
                    label: item.areaName
                  })) || []
                }
                withAsterisk
                searchable
                disabled={loadingMasterArea}
                error={errors.creatorCode?.message}
                value={field.value}
                onChange={(value) => field.onChange(value || '')}
              />
            )}
          />
          <Controller
            name='trainingType'
            control={control}
            render={({ field }) => (
              <Select
                label='Training Type'
                placeholder='Select training type'
                data={
                  dataTrainingType?.data?.map(
                    (item: { id: number; option: string }) => item.option
                  ) || []
                }
                searchable
                disabled={loadingTrainingType}
                error={errors.trainingType?.message}
                value={field.value}
                onChange={(value) => field.onChange(value || '')}
                withAsterisk
              />
            )}
          />
          <Controller
            name='competencyCategory'
            control={control}
            render={({ field }) => {
              const selectedValues =
                field.value && typeof field.value === 'string'
                  ? field.value.split(',').filter(Boolean)
                  : [];
              const availableOptions =
                dataCompetencyCategories?.data?.map(
                  (item: { competencyCategoryDesc: string }) => item.competencyCategoryDesc
                ) || [];

              const allOptions = [...new Set([...selectedValues, ...availableOptions])];

              return (
                <MultiSelect
                  label='Competency Category'
                  disabled={loadingCompetencyCategories}
                  placeholder={
                    loadingCompetencyCategories ? 'Loading...' : 'Select competency category'
                  }
                  data={allOptions}
                  withAsterisk
                  searchable
                  error={errors.competencyCategory?.message}
                  value={selectedValues}
                  onChange={(value) => field.onChange(value.join(',') || '')}
                />
              );
            }}
          />

          <Controller
            name='targetAudience'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Target Audience (optional)'
                placeholder='Enter target audience'
                error={errors.targetAudience?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='programCategory'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Program Category (optional)'
                placeholder='Enter program category'
                error={errors.programCategory?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='targetBinusian'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Target Binusian (optional)'
                placeholder='Enter target binusian'
                error={errors.targetBinusian?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='businessUnits'
            control={control}
            render={({ field }) => {
              const hasAllSelected = field.value?.includes('All');

              return (
                <MultiSelect
                  label='Business Unit (optional)'
                  placeholder='Enter business unit'
                  data={[
                    { label: 'All', value: 'All' },
                    ...(dataBusinessUnit?.data?.map((item: { buCode: string; buName: string }) => {
                      return {
                        label: item.buName,
                        value: item.buCode,
                        disabled: hasAllSelected
                      };
                    }) || [])
                  ]}
                  disabled={loadingBusinessUnit}
                  error={errors.businessUnits?.message}
                  searchable
                  onChange={(value) => {
                    // If 'All' is being added and wasn't there before
                    if (value.includes('All') && !field.value?.includes('All')) {
                      field.onChange(['All']); // Only select 'All', remove other selections
                    }
                    // If 'All' is already selected and user selects something else
                    else if (field.value?.includes('All') && value.length > 1) {
                      field.onChange(value.filter((v) => v !== 'All')); // Remove 'All' when selecting others
                    }
                    // Normal case
                    else {
                      field.onChange(value);
                    }
                  }}
                  value={field.value || []}
                />
              );
            }}
          />

          <Controller
            name='specificJobBand'
            control={control}
            render={({ field }) => (
              <MultiSelect
                label='Specific Job Band (optional)'
                placeholder='Enter specific job band'
                data={dataBands?.data?.map((item: { bandName: string }) => item.bandName) || []}
                disabled={loadingBands}
                error={errors.specificJobBand?.message}
                value={field.value && typeof field.value === 'string' ? field.value.split(',') : []}
                searchable
                onChange={(value) => field.onChange(value.join(',') || '')}
              />
            )}
          />

          <Controller
            name='trainingLocation'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Training Location (optional)'
                placeholder='Enter training location'
                error={errors.trainingLocation?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='learningObjectives'
            control={control}
            render={({ field }) => (
              <RichTextEditorInput
                required
                label='Learning Objectives'
                placeholder='Enter learning objectives'
                error={errors.learningObjectives?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='learningContent'
            control={control}
            render={({ field }) => (
              <RichTextEditorInput
                label='Learning Content (optional)'
                placeholder='Enter learning content'
                error={errors.learningContent?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='learningHours'
            control={control}
            render={({ field }) => (
              <TextInput
                withAsterisk
                label='Learning Hours'
                placeholder='Enter total learning hours'
                error={errors.learningHours?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='levelOfTraining'
            control={control}
            render={({ field }) => (
              <Select
                label='Level (optional)'
                placeholder='Select level'
                data={
                  dataLevelOfTraining?.data.map((item: { id: number; option: string }) => ({
                    value: item.option,
                    label: item.option
                  })) || []
                }
                searchable
                clearable
                disabled={loadingLevelOfTraining}
                error={errors.levelOfTraining?.message}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value || '');
                  // Sync the ID to bcldLevel
                  if (value) {
                    const selectedItem = dataLevelOfTraining?.data.find(
                      (item: { id: number; option: string }) => item.option === value
                    );
                    if (selectedItem) {
                      setValue('bcldLevel', selectedItem.id.toString());
                    }
                  } else {
                    // If cleared, set bcldLevel to '1' as default
                    setValue('bcldLevel', '1');
                  }
                }}
              />
            )}
          />

          <Controller
            name='fieldOrganizer'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Organizer (optional)'
                placeholder='Enter organizer'
                error={errors.fieldOrganizer?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='room'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Room (optional)'
                placeholder='Enter room'
                error={errors.room?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='passingFormula'
            control={control}
            render={({ field }) => (
              <>
                <Radio.Group
                  label='Passing Formula/Grade'
                  value={field.value}
                  withAsterisk
                  onChange={(value) => field.onChange(value || '')}
                >
                  <Group mt='xs'>
                    {dataPassingFormula?.data.map(
                      (item: { id: number; option: string; optionDesc: string }) => (
                        <Group key={item.id} gap={0}>
                          <Radio value={item.option} label={item.option} />
                          <InputInformation description={item.optionDesc} />
                        </Group>
                      )
                    )}
                  </Group>
                </Radio.Group>
                {errors.passingFormula?.message && (
                  <Text size='xs' c={'red'}>
                    {errors.passingFormula?.message}
                  </Text>
                )}
              </>
            )}
          />

          <Controller
            name='weightAttendance'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Weight Attendance (optional)'
                placeholder='Enter Weight Attendance'
                error={errors.weightAttendance?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='weightPreTest'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Weight Pre-Test (optional)'
                placeholder='Enter Weight Pre-Test'
                error={errors.weightPreTest?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='weightPostTest'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Weight Post-Test (optional)'
                placeholder='Enter Weight Post-Test'
                error={errors.weightPostTest?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='weightALP'
            control={control}
            render={({ field }) => (
              <TextInput
                label='Weight ALP (optional)'
                placeholder='Enter Weight ALP'
                error={errors.weightALP?.message}
                {...field}
              />
            )}
          />

          <Controller
            name='waReminder'
            control={control}
            render={({ field }) => (
              <SegmentedInput
                label='WhatsApp Reminder (optional)'
                options={YES_NO_OPTION}
                error={errors.waReminder?.message}
                value={YES_NO_OPTION.find((item) => field.value === item.label)?.value || ''}
                onChange={(value: string) =>
                  field.onChange(YES_NO_OPTION.find((item) => item.value === value)?.label || '')
                }
              />
            )}
          />
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default BCLDCustomFieldForm;
